import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Button from "shared/Button";
import FormInput from "shared/FormInput";
import { resetPassword } from "core/Services/AuthServices";

// Type for form data
interface NewPasswordFormData {
  newPassword: string;
  confirmPassword: string;
}

const NewPasswordReset = () => {
  const navigate = useNavigate();
  const { token, email } = useParams<{ token: string; email: string }>();
  const [showPassword, setShowPassword] = useState({
    newPassword: false,
    confirmPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  // Password validation schema
  const NewPasswordSchema = yup.object({
    newPassword: yup
      .string()
      .required(t("auth.validation.newPasswordRequired"))
      .min(8, t("auth.validation.passwordMinLength"))
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
        t("auth.validation.passwordRequirements")
      ),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref("newPassword")], t("auth.validation.passwordsMustMatch"))
      .required(t("auth.validation.confirmPasswordRequired")),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NewPasswordFormData>({
    resolver: yupResolver(NewPasswordSchema),
  });

  // Toggle password visibility
  const togglePasswordVisibility = (field: keyof typeof showPassword) => {
    setShowPassword((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Submit handler
  const onSubmit = async (data: NewPasswordFormData) => {
    if (!token) {
      toast.error(t("auth.invalidResetToken"));
      return;
    }

    setIsLoading(true);

    try {
      toast.success(t("auth.passwordResetSuccess"));
      resetPassword(
        email?.replace(":", "")!,
        token.replace(":", ""),
        data.newPassword,
        data.confirmPassword
      );
      navigate("/");
    } catch (error: any) {
      toast.error(error.message || t("auth.passwordResetError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 flex items-center justify-center ">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 space-y-6">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <Lock
            size={64}
            className="text-blue-500 opacity-80"
            strokeWidth={1.5}
          />
        </div>

        {/* Title */}
        <div className="text-center space-y-2 mb-6">
          <h2 className="text-2xl font-bold text-gray-800">
            {t("auth.setNewPassword")}
          </h2>
          <p className="text-gray-500 text-sm">
            {t("auth.createStrongPassword")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* New Password Input */}
          <FormInput
            label={t("auth.newPassword")}
            name="newPassword"
            type={showPassword.newPassword ? "text" : "password"}
            register={register}
            errors={errors}
            placeholder={t("auth.enterNewPassword")}
            icon={
              showPassword.newPassword ? (
                <Eye
                  size={20}
                  className="cursor-pointer text-gray-500 hover:text-blue-600"
                  onClick={() => togglePasswordVisibility("newPassword")}
                />
              ) : (
                <EyeOff
                  size={20}
                  className="cursor-pointer text-gray-500 hover:text-blue-600"
                  onClick={() => togglePasswordVisibility("newPassword")}
                />
              )
            }
          />

          {/* Confirm Password Input */}
          <FormInput
            label={t("auth.confirmNewPassword")}
            name="confirmPassword"
            type={showPassword.confirmPassword ? "text" : "password"}
            register={register}
            errors={errors}
            placeholder={t("auth.confirmNewPassword")}
            icon={
              showPassword.confirmPassword ? (
                <Eye
                  size={20}
                  className="cursor-pointer text-gray-500 hover:text-blue-600"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                />
              ) : (
                <EyeOff
                  size={20}
                  className="cursor-pointer text-gray-500 hover:text-blue-600"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                />
              )
            }
          />

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            isLoading={isLoading}
            disabled={isLoading}
            className="mt-6"
          >
            {isLoading ? t("auth.resetting") : t("auth.setNewPassword")}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default NewPasswordReset;
